import React from 'react';
import { useSelector } from 'react-redux'
import { HashLink as Link } from 'react-router-hash-link';
import { GlobalStore } from '../../../store';
import User from '../../../models/User';
import { Cross, Plus } from '../../../viewcomponents/FontAwesome';

export default (): JSX.Element | null => {

  const user = useSelector<GlobalStore, User | null>(store => store.user);

  return (
    user?.isAdministrator || user?.isTeacher()
      ? <div className="card p-3 mb-3">
          <h2 id="teacher">Teacher</h2>

          <p>
            Teachers are in charge of specific queues. On top of everything an
            <Link smooth to="/help#assistant"> assistant</Link> can do, they manage who runs the queue and the
            queue itself.
          </p>
          <p>
            All of this lives on the <Link smooth to="/administration">Administration</Link> tab.
            Go there first and <strong>choose your queue</strong> from the list of your queues — its options then
            appear below. Everything described here starts that way.
          </p>

          <h3 id="addTeacherOrAssistant">Add or remove teachers and assistants</h3>
          <p>
            Under the selected queue you'll find an <em>Add teacher</em> and an <em>Add assistant</em> field.
            Type the person's username and click <em>Add</em> — they appear in the list below, with their
            privileges effective immediately. It looks like this:
          </p>

          {/* Non-interactive illustration of the add/remove controls */}
          <div className="col-12 col-lg-8 p-0 mb-2">
            <div className="input-group">
              <input type="text" className="form-control" placeholder="Add teacher" readOnly />
              <div className="input-group-append">
                <button className="btn theme text-white" type="button" tabIndex={-1}><Plus /> Add</button>
              </div>
            </div>
          </div>
          <table className="table table-striped" style={{ maxWidth: '32rem' }}>
            <thead>
              <tr><th>Name</th><th>Username</th></tr>
            </thead>
            <tbody>
              <tr>
                <td>Erik Lundgren</td>
                <td>erikl <Cross color="red" title="Remove teacher" /></td>
              </tr>
            </tbody>
          </table>
          <p id="removeTeacherOrAssistant">
            To remove someone, click the red cross next to their username. Once their name leaves the list, so
            do their privileges.
          </p>

          <h3 id="hide">Hide a queue</h3>
          <p>
            Hiding a queue both <Link smooth to="/help#purge">purges</Link> and hides it — useful when a queue
            will have no labs for a while. With your queue selected, click <em>Hide queue</em> and confirm.
            To bring it back, click <em>Reveal queue</em> in the same place.
          </p>

          <h3 id="removeQueue">Remove a queue</h3>
          <p>
            Removing a queue deletes it from the system along with all its privileges — this cannot be undone.
            With your queue selected, click <em>Remove queue</em> and confirm.
            To get a removed queue back you'll have to contact an administrator.
          </p>

          <h3 id="statistics">Statistics and bookings</h3>
          <p>
            Besides Administration, teachers have two more tabs in the top navigation you can switch to:
            <em> Statistics</em> and <em>Bookings</em>.
          </p>
          <p>
            On the <Link smooth to="/statistics">Statistics</Link> tab, pick a queue and a <em>From</em>–<em>Until</em> range
            and click <em>Load statistics</em>. You get a per-session summary — students helped, presentations, and
            average and maximum wait times — and clicking a session expands its charts and an hourly table
            (downloadable as PNG or CSV). A <em>Queue log</em> view shows the raw join/leave events as JSON, also
            available through the <Link smooth to="/help#api">REST API</Link>.
          </p>
          <p id="bookings">
            On the <Link smooth to="/bookings">Bookings</Link> tab you manage pre-booked lab slots. Pick a queue and
            upload a list of slots as JSON; each student is matched by their KTH username or provider ID. On the day,
            within 30 minutes of their slot, that student's queue entry shows a booking badge and is promoted to the
            top of the queue. Uploading again updates and adds bookings, and you can browse them by date.
          </p>

          <h3 id="api">Automation with API keys</h3>
          <p>
            Some tasks can be automated with the REST API — for example uploading booked lab slots from a script
            instead of pasting JSON by hand. Go to the <Link smooth to="/api">API</Link> page to generate a key
            and find instructions for the available endpoints.
          </p>
        </div>
      : null
  );
};
